<template>
  <div class="auto-mode-wrapper" :class="{ 'exiting': isExiting }">
    <!-- Border around entire viewport -->
    <div class="auto-mode-border"></div>

    <!-- Full-width banner below navbar -->
    <div class="auto-mode-banner">
      <div class="auto-mode-banner-left">
        <i class="fa fa-rocket"></i>
        <span class="auto-mode-label">Auto Mode</span>
        <span v-if="currentQueue" class="queue-name">- {{ currentQueue }}</span>
        <span v-if="filterSummary" class="filter-summary">
          | <template v-for="(part, i) in filterParts"><template v-if="i > 0"> | </template><span
            :key="i" v-b-tooltip.hover="part.tooltip" :class="{ 'has-tooltip': !!part.tooltip }">{{ part.text }}</span></template>
        </span>
      </div>
      <button class="auto-mode-exit-btn" @click="exitAutoMode">
        Exit Auto Mode
        <i class="fa fa-close"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import AutoModeService from '@/services/AutoModeService';

@Component
export default class AutoModeIndicator extends Vue {
  private isExiting = false;

  get currentQueue(): string | null {
    return this.$store.currentAutoModeQueue;
  }

  get filterParts(): { text: string; tooltip?: string }[] {
    const parts: { text: string; tooltip?: string }[] = [];

    const queues = AutoModeService.getSavedSpecificQueues();
    if (queues.length) {
      parts.push({ text: queues.join(', ') });
    }

    const stores = AutoModeService.getSavedStoreFilters();
    if (stores.length) {
      parts.push({
        text: `${stores.length} store${stores.length > 1 ? 's' : ''}`,
        tooltip: stores.map(s => s.name).join(', ')
      });
    }

    const programs = AutoModeService.getSavedProgramFilters();
    if (programs.length) {
      parts.push({
        text: `${programs.length} program${programs.length > 1 ? 's' : ''}`,
        tooltip: programs.map(p => p.name).join(', ')
      });
    }

    return parts;
  }

  get filterSummary(): boolean {
    return this.filterParts.length > 0;
  }

  mounted(): void {
    document.addEventListener('keyup', this.handleEscKey);
  }

  beforeDestroy(): void {
    document.removeEventListener('keyup', this.handleEscKey);
  }

  handleEscKey(e: KeyboardEvent): void {
    if (e.code === 'Escape' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      this.exitAutoMode();
    }
  }

  exitAutoMode(): void {
    // Trigger exit animation
    this.isExiting = true;

    // Wait for animation to complete before clearing state
    setTimeout(() => {
      this.$store.autoModeEnabled = false;
      this.$store.currentAutoModeQueue = null;
      this.$store.lastAutoModeResponse = null;

      this.$notification({
        message: 'Auto Mode has been exited',
        timeout: 3000,
        icon: "now-ui-icons ui-1_bell-53",
        horizontalAlign: "center",
        verticalAlign: "top",
        type: "info"
      });
    }, 400); // Match animation duration
  }
}
</script>

<style scoped>
.auto-mode-wrapper {
  pointer-events: none;
}

.auto-mode-border {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 4px solid #ff6b35;
  z-index: 9998;
  pointer-events: none;
  animation: border-appear 0.5s ease-out;
}

@keyframes border-appear {
  0% {
    opacity: 0;
    border-width: 0px;
  }
  50% {
    opacity: 1;
    border-width: 8px;
  }
  100% {
    border-width: 4px;
  }
}

.auto-mode-banner {
  position: fixed;
  top: 4px;
  left: 4px;
  right: 4px;
  height: 36px;
  background: #ff6b35;
  color: white;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  pointer-events: auto;
  animation: banner-slide-down 0.5s ease-out;
}

.auto-mode-banner-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

  .fa-rocket {
    font-size: 18px;
  }

  .queue-name {
    font-weight: 600;
  }

  .filter-summary {
    font-weight: 400;
    font-size: 14px;
    opacity: 0.9;

    .has-tooltip {
      cursor: help;
      border-bottom: 1px dotted rgba(255, 255, 255, 0.6);
    }
  }
}

.auto-mode-exit-btn {
  position: absolute;
  right: 16px;
  background: #e85e2b;
  border: 1px solid #e85e2b;
  color: white;
  padding: 2px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #d45221;
    border-color: #d45221;
  }
}

@keyframes banner-slide-down {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  60% {
    transform: translateY(2px);
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Exit animations */
.exiting .auto-mode-border {
  animation: border-disappear 0.4s ease-in forwards;
}

.exiting .auto-mode-banner {
  animation: banner-slide-up 0.4s ease-in forwards;
}

@keyframes border-disappear {
  0% {
    border-width: 4px;
    opacity: 1;
  }
  50% {
    border-width: 8px;
  }
  100% {
    border-width: 0px;
    opacity: 0;
  }
}

@keyframes banner-slide-up {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-100%);
    opacity: 0;
  }
}
</style>
